
import React, { useState } from 'react';
import { Trash2, Star, Tag } from 'lucide-react';
import { Product, Language, Translations } from '../types';
import { EditableProductText } from './EditableProductText';
import { ImageWithLoader } from './ImageWithLoader';
import { useEditor } from '../contexts/EditorContext';

interface ProductCardProps {
  product: Product;
  currentLang: Language;
  t: Translations[Language];
  onViewDetails?: (id: string) => void;
  onToggleCompare?: (id: string) => void;
  onDelete?: (id: string) => void; 
  isCompareSelected?: boolean;
  variant?: 'default' | 'simple';
}

export const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  currentLang, 
  t, 
  onViewDetails,
  onToggleCompare,
  onDelete,
  isCompareSelected = false,
  variant = 'default'
}) => {
  const { isEditMode, deleteProduct, toggleProductFeatured } = useEditor();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const handleDetailsClick = (e: React.MouseEvent) => {
    if (!isEditMode && onViewDetails) {
      onViewDetails(product.id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirmDelete) {
      if (onDelete) {
        onDelete(product.id);
      } else {
        deleteProduct(product.id);
      }
      setShowConfirmDelete(false);
    } else {
      setShowConfirmDelete(true);
      setTimeout(() => setShowConfirmDelete(false), 3000);
    }
  };

  const handleToggleFeatured = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleProductFeatured(product.id);
  };

  const isFeatured = product.featured !== false; 

  return (
    <div 
      className={`group bg-white rounded-xl md:rounded-3xl shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col h-full overflow-hidden relative border border-slate-100 ${isCompareSelected ? 'ring-2 ring-primary-500' : ''} ${isEditMode ? '' : 'cursor-pointer hover:-translate-y-1'}`} 
      onClick={handleDetailsClick}
    >
      {/* Product Image Area */}
      <div className="relative aspect-square bg-slate-50 overflow-hidden group">
        
        {/* Compare Button */}
        {!isEditMode && onToggleCompare && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleCompare(product.id);
            }}
            className={`absolute top-2 left-2 md:top-3 md:left-3 z-30 p-1.5 rounded-lg shadow-md transition-all flex items-center justify-center backdrop-blur-sm ${
              isCompareSelected ? 'bg-primary-500 text-white' : 'bg-white/80 text-slate-400 hover:text-primary-500 hover:bg-white'
            }`}
            title="Compare"
          >
            <div className={`w-3.5 h-3.5 md:w-4 md:h-4 border-2 rounded-sm flex items-center justify-center transition-colors ${isCompareSelected ? 'border-white bg-primary-500' : 'border-slate-400'}`}>
              {isCompareSelected && <svg viewBox="0 0 24 24" fill="none" className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
            </div>
          </button>
        )}
        
        {isEditMode && (
          <div className="absolute top-2 right-2 md:top-3 md:right-3 z-30 flex gap-1.5">
             <button
                onClick={handleToggleFeatured}
                className={`p-1.5 rounded-lg shadow-md transition-all flex items-center justify-center backdrop-blur-sm ${
                   isFeatured ? 'bg-amber-400 text-white' : 'bg-white/60 text-slate-400 hover:text-slate-600'
                }`}
                title="Toggle Home Display"
             >
                <Star size={12} className="md:w-3.5 md:h-3.5" fill={isFeatured ? "currentColor" : "none"} />
             </button>

             <button 
                onClick={handleDelete}
                className={`text-white p-1.5 rounded-lg shadow-md transition-all flex items-center gap-1.5 backdrop-blur-sm ${
                   showConfirmDelete ? 'bg-red-700 w-auto px-2' : 'bg-red-600/80 hover:bg-red-700'
                }`}
              >
                <Trash2 size={12} className="md:w-3.5 md:h-3.5" />
                {showConfirmDelete && <span className="text-[10px] font-bold uppercase">Del?</span>}
              </button>
          </div>
        )}

        <div className="w-full h-full flex items-center justify-center overflow-hidden">
            <ImageWithLoader 
              src={product.imageUrl} 
              alt={`${product.name[currentLang]} - High-Precision Coriolis Mass Flowmeter & Industrial Sensor`} 
              scale={product.imageScale || 1.0}
              style={{ objectPosition: product.imagePosition || '50% 50%' }}
              className={`w-full h-full object-cover transition-transform duration-700 ${!isEditMode && 'group-hover:scale-105'}`}
              containerClassName="w-full h-full"
            />
        </div>

        <div className="absolute inset-0 bg-primary-900/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      </div>
      
      {/* Product Info Area - Optimized for Mobile Grid */}
      <div className="p-3 md:p-5 flex flex-col flex-grow bg-white border-t border-slate-50">
        {variant !== 'simple' && (
          <div className="flex items-center flex-wrap gap-1.5 mb-2 md:mb-3">
              <div className="px-1.5 py-0.5 text-[8px] md:text-[9px] font-black text-primary-600 bg-primary-50 border border-primary-100 rounded uppercase tracking-wider flex items-center gap-1 truncate max-w-full">
                  <Tag size={10} className="text-primary-400 shrink-0" />
                  <div className="truncate">
                    <EditableProductText 
                      productId={product.id} 
                      field="category" 
                      lang={currentLang} 
                      value={typeof product.category === 'string' ? product.category : (product.category?.[currentLang] || product.category?.[Language.EN] || 'Industrial')} 
                    />
                  </div>
              </div>
              {product.code && (
                <span className="text-[8px] md:text-[9px] font-mono font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 hidden sm:inline-block">
                   {product.code}
                </span>
              )}
          </div>
        )}

        <h3 className="text-xs sm:text-sm md:text-base font-bold text-slate-900 leading-tight transition-colors group-hover:text-primary-600 line-clamp-2 min-h-[2.5em]">
          <EditableProductText 
             productId={product.id} 
             field="name" 
             lang={currentLang} 
             value={product.name[currentLang]} 
          />
        </h3>
        
        <div className="mt-auto pt-3 md:pt-4 flex items-center justify-between">
           <span className="text-primary-600 text-[9px] md:text-[10px] font-black uppercase tracking-widest opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all transform md:translate-y-2 md:group-hover:translate-y-0">
             View Details
           </span>
           <div className="w-1.5 h-1.5 rounded-full bg-primary-200 group-hover:bg-primary-500 transition-colors"></div>
        </div>
      </div>
    </div>
  );
};
